/**
 * Centralized API client for adde.app frontend.
 * Barcha so'rovlarda Accept: application/json va Authorization: Bearer <token> yuboriladi.
 * 401 — token tozalanadi va login sahifasiga yo'naltirish.
 * 4xx/5xx — JSON dagi message foydalanuvchiga ko'rsatiladi.
 *
 * CORS: Brauzer avval OPTIONS (preflight) yuboradi → 204; keyin asosiy GET/POST
 * yuboriladi (Authorization va Accept header bilan). Agar faqat OPTIONS ko‘rinsa,
 * GET ketmasa — backend CORS da Access-Control-Allow-Origin, Allow-Methods,
 * Allow-Headers: Authorization qilganini tekshiring (api.adde.app).
 */
import { API_BASE_URL, buildAbsoluteUrl } from '../config/apiConfig';
import { endpoints } from './endpoints';

const baseURL = API_BASE_URL;
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const LEGACY_TOKEN_KEY = 'auth_token';
const SESSION_ID_KEY = 'auth_session_id';
const USER_STORAGE_KEY = 'adminUser';
const ROLE_STORAGE_KEY = 'adminRole';
const EFFECTIVE_ROLE_KEY = 'effectiveRoleSwitch';
const EFFECTIVE_AS_USER_ID_KEY = 'effectiveAsUserIdSwitch';
const DEFAULT_TIMEOUT_MS = 30000; // 30 soniya - sekin backend uchun
const MAX_RETRIES = 2; // 2 marta qayta urinish
const OFFLINE_CACHE_PREFIX = 'adde_api_cache_v1:';
const OFFLINE_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 kun

let refreshPromise = null;

function getOfflineCacheKey(url) {
  return `${OFFLINE_CACHE_PREFIX}${encodeURIComponent(String(url || ''))}`;
}

function saveOfflineCache(url, data) {
  if (typeof window === 'undefined') return;
  try {
    const key = getOfflineCacheKey(url);
    const payload = { ts: Date.now(), data };
    window.localStorage?.setItem?.(key, JSON.stringify(payload));
  } catch {
    // ignore quota/storage errors
  }
}

function readOfflineCache(url) {
  if (typeof window === 'undefined') return null;
  try {
    const key = getOfflineCacheKey(url);
    const raw = window.localStorage?.getItem?.(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    if (!ts || Date.now() - ts > OFFLINE_CACHE_TTL_MS) return null;
    if (parsed && Object.prototype.hasOwnProperty.call(parsed, 'data')) {
      return parsed.data;
    }
    return null;
  } catch {
    return null;
  }
}

function getAuthHeader() {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage?.getItem?.(ACCESS_TOKEN_KEY) ?? window.localStorage?.getItem?.(LEGACY_TOKEN_KEY);
  const token = raw ? String(raw).trim() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getDeviceHeaders() {
  if (typeof window === 'undefined') return {};
  const ua = window.navigator?.userAgent ?? '';
  const isMobile = /Android|iPhone|iPad|Mobile/i.test(ua);
  return {
    'X-Device-Type': isMobile ? 'mobile' : 'web',
    'X-Device-Name': isMobile ? 'ADDE Mobile Browser' : 'ADDE Web Browser',
  };
}

function setTokens({ accessToken, refreshToken, sessionId = null }) {
  try {
    if (typeof window === 'undefined') return;
    if (accessToken) {
      window.localStorage?.setItem?.(ACCESS_TOKEN_KEY, String(accessToken));
      window.localStorage?.removeItem?.(LEGACY_TOKEN_KEY);
    }
    if (refreshToken) {
      window.localStorage?.setItem?.(REFRESH_TOKEN_KEY, String(refreshToken));
    }
    if (sessionId) {
      window.localStorage?.setItem?.(SESSION_ID_KEY, String(sessionId));
    } else {
      window.localStorage?.removeItem?.(SESSION_ID_KEY);
    }
  } catch {}
}

function getRefreshToken() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage?.getItem?.(REFRESH_TOKEN_KEY);
    return raw ? String(raw).trim() : null;
  } catch {
    return null;
  }
}

function clearAuthAndRedirect() {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage?.removeItem?.(ACCESS_TOKEN_KEY);
      window.localStorage?.removeItem?.(REFRESH_TOKEN_KEY);
      window.localStorage?.removeItem?.(LEGACY_TOKEN_KEY);
      window.localStorage?.removeItem?.(SESSION_ID_KEY);
      window.localStorage?.removeItem?.(USER_STORAGE_KEY);
      window.localStorage?.removeItem?.(ROLE_STORAGE_KEY);
      window.sessionStorage?.removeItem?.(ROLE_STORAGE_KEY);
      window.localStorage?.removeItem?.(EFFECTIVE_ROLE_KEY);
      window.localStorage?.removeItem?.(EFFECTIVE_AS_USER_ID_KEY);
      window.sessionStorage?.removeItem?.(EFFECTIVE_ROLE_KEY);
      window.sessionStorage?.removeItem?.(EFFECTIVE_AS_USER_ID_KEY);
      const isDesktop = /addedesktop/i.test(String(window.navigator?.userAgent || ''));
      window.location.assign(isDesktop ? '/desktop-login' : '/');
    }
  } catch {}
}

function collectFieldValidationMessages(data) {
  const parts = [];
  const sources = [data?.errors, data?.meta?.errors];
  for (const src of sources) {
    if (!src || typeof src !== 'object' || Array.isArray(src)) continue;
    for (const v of Object.values(src)) {
      if (Array.isArray(v)) {
        v.forEach((x) => {
          if (x != null && String(x).trim()) parts.push(String(x));
        });
      } else if (v != null && String(v).trim()) {
        parts.push(String(v));
      }
    }
  }
  return parts;
}

function extractErrorMessage(data, fallback) {
  const validationParts = collectFieldValidationMessages(data);
  if (validationParts.length) return validationParts.join(' ');

  const msg = data?.message ?? data?.error ?? data?.msg;
  if (msg != null && typeof msg === 'string' && String(msg).trim()) return msg;
  if (data?.data && typeof data.data === 'object' && data.data?.message) return String(data.data.message);
  if (Array.isArray(data?.errors) && data.errors[0]?.message) return String(data.errors[0].message);
  return fallback ?? 'Network error';
}

function buildUrl(path) {
  return buildAbsoluteUrl(path);
}

function buildRequestUrls(path, params = {}) {
  const urls = [];
  const appendQuery = (rawUrl) => {
    const queryParams = params && typeof params === 'object' ? params : {};
    const search = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => {
      if (v !== undefined && v !== '') search.set(k, String(v));
    });
    const q = search.toString();
    if (!q) return rawUrl;
    return rawUrl + (rawUrl.includes('?') ? '&' : '?') + q;
  };

  const primary = appendQuery(buildUrl(path));
  urls.push(primary);

  if (typeof window !== 'undefined') {
    const localFallback = appendQuery(new URL(path && path.startsWith('/') ? path : `/${path || ''}`, window.location.origin).toString());
    if (!urls.includes(localFallback)) {
      urls.push(localFallback);
    }
  }

  return urls;
}

function isNetworkLikeError(err) {
  if (!err) return false;
  const name = String(err?.name ?? '').toLowerCase();
  const message = String(err?.message ?? '').toLowerCase();
  if (name === 'aborterror') return false;
  if (message.includes('failed to fetch')) return true;
  if (message.includes('network')) return true;
  if (message.includes('load failed')) return true;
  if (name === 'typeerror' && (typeof err !== 'object' || err === null || !('status' in err))) return true;
  return false;
}

function isAuthTokenFailure(err) {
  const d = err?.data ?? null;
  const meta = d?.meta && typeof d.meta === 'object' ? d.meta : null;
  const candidates = [
    d?.code,
    d?.error,
    meta?.code,
    meta?.auth_reason,
    meta?.authReason,
  ]
    .filter((x) => x != null && String(x).trim() !== '')
    .map((x) => String(x).trim().toUpperCase());

  // Business-level 401 (PASSWORD_INVALID, PIN_INVALID, etc.) sessiyani yiqitmasligi kerak.
  for (const c of candidates) {
    if (!['UNAUTHENTICATED', 'TOKEN_EXPIRED', 'INVALID_TOKEN'].includes(c)) {
      return false;
    }
    return true;
  }

  const msg = String(d?.message ?? err?.message ?? '').toLowerCase();
  return msg.includes('unauthenticated') || msg.includes('token') || msg.includes('expired');
}

async function handleResponse(res, skipAuth = false) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // ignore
  }
  if (!res.ok) {
    const message = extractErrorMessage(data, res.statusText);
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    err.code = data?.code ?? data?.error_code ?? null;
    err.skipAuth = skipAuth;
    throw err;
  }
  return data != null ? data : {};
}

async function refreshTokens() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthAndRedirect();
    throw new Error('Unauthenticated');
  }

  refreshPromise = (async () => {
    const previousRefresh = refreshToken;
    const urlStr = buildUrl(endpoints.auth.refresh);
    const res = await fetch(urlStr, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${refreshToken}`,
        ...getDeviceHeaders(),
      },
      body: JSON.stringify({}),
    });
    const data = await handleResponse(res, true);
    const payload = data?.data ?? data;
    const accessToken = payload?.access_token ?? payload?.accessToken ?? payload?.token ?? null;
    const newRefreshToken = payload?.refresh_token ?? payload?.refreshToken ?? null;
    const sessionId = payload?.session_id ?? payload?.sessionId ?? null;
    if (!accessToken) {
      throw new Error('Invalid refresh response');
    }
    const effectiveRefresh = newRefreshToken ? String(newRefreshToken).trim() : previousRefresh;
    if (!effectiveRefresh) {
      throw new Error('Invalid refresh response');
    }
    const normalizedAccess = String(accessToken).trim();
    setTokens({ accessToken: normalizedAccess, refreshToken: effectiveRefresh, sessionId: sessionId ? String(sessionId).trim() : null });
    return { accessToken: normalizedAccess, refreshToken: effectiveRefresh };
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function request(path, { method = 'GET', body = null, params = {}, timeout = DEFAULT_TIMEOUT_MS, retries = MAX_RETRIES, skipAuth = false } = {}) {
  const requestUrls = buildRequestUrls(path, params);
  let urlIndex = 0;

  let lastError = null;
  let refreshedOnce = false;
  let triedWithoutDeviceHeaders = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const headers = {
        Accept: 'application/json',
        ...(triedWithoutDeviceHeaders ? {} : getDeviceHeaders()),
        ...(skipAuth ? {} : getAuthHeader()),
      };
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      if ((body != null || method !== 'GET') && !isFormData) {
        headers['Content-Type'] = 'application/json';
      }
      const res = await fetch(requestUrls[urlIndex], {
        method,
        signal: controller.signal,
        headers,
        ...(body != null && { body: isFormData ? body : JSON.stringify(body) }),
      });
      clearTimeout(id);
      const data = await handleResponse(res, skipAuth);
      if (String(method || 'GET').toUpperCase() === 'GET') {
        saveOfflineCache(requestUrls[urlIndex], data);
      }
      return data;
    } catch (err) {
      clearTimeout(id);
      lastError = err;

      const status = err?.status;
      /** Laravel / Sanctum: 401 sabablari turlicha matn — bitta «unauthenticated» tekshiruvi refreshni o'tkazib yuborishi mumkin. */
      const shouldTryRefresh = !skipAuth && status === 401 && !!getRefreshToken() && isAuthTokenFailure(err);
      if (shouldTryRefresh && !refreshedOnce) {
        try {
          await refreshTokens();
          refreshedOnce = true;
          attempt--;
          continue;
        } catch {
          clearAuthAndRedirect();
          throw err;
        }
      }

      if (!skipAuth && status === 401) {
        clearAuthAndRedirect();
        throw err;
      }

      const networkLikeError = isNetworkLikeError(err);
      if (networkLikeError && String(method || 'GET').toUpperCase() === 'GET') {
        const cached = readOfflineCache(requestUrls[urlIndex]);
        if (cached != null) {
          return cached;
        }
      }
      if (networkLikeError && urlIndex < requestUrls.length - 1) {
        urlIndex += 1;
        if (attempt < retries) {
          continue;
        }
      }
      // Ba'zi mobil brauzerlarda custom header (X-Device-*) preflightni yiqitishi mumkin.
      // Shu holatda keyingi urinishda ushbu headerlarsiz qayta so'rov yuboramiz.
      if (networkLikeError && !triedWithoutDeviceHeaders) {
        triedWithoutDeviceHeaders = true;
        if (attempt < retries) {
          continue;
        }
      }

      // Timeout yoki network error bo'lsa va hali retry qoldi bo'lsa
      if ((err.name === 'AbortError' || err.message?.includes('timeout') || networkLikeError) && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(4000, 200 * 2 ** attempt)));
        continue;
      }
      if (err.name === 'AbortError') throw new Error('Request timeout');
      throw err;
    }
  }
  throw lastError || new Error('Request failed');
}

/**
 * Fayl yuklab olish (Excel/CSV) — JSON emas, Blob qaytaradi.
 */
async function requestBlob(path, { method = 'POST', body = null, timeout = DEFAULT_TIMEOUT_MS, retries = MAX_RETRIES, skipAuth = false } = {}) {
  const requestUrls = buildRequestUrls(path);
  let urlIndex = 0;
  let lastError = null;
  let refreshedOnce = false;
  let triedWithoutDeviceHeaders = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const headers = {
        Accept: '*/*',
        ...(triedWithoutDeviceHeaders ? {} : getDeviceHeaders()),
        ...(skipAuth ? {} : getAuthHeader()),
      };
      if (body != null) {
        headers['Content-Type'] = 'application/json';
      }
      const res = await fetch(requestUrls[urlIndex], {
        method,
        signal: controller.signal,
        headers,
        ...(body != null && { body: JSON.stringify(body) }),
      });
      clearTimeout(id);
      if (!res.ok) {
        const text = await res.text();
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          // ignore
        }
        const message = extractErrorMessage(data, res.statusText);
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        err.skipAuth = skipAuth;
        const shouldTryRefresh = !skipAuth && res.status === 401 && !!getRefreshToken() && isAuthTokenFailure(err);
        if (shouldTryRefresh && !refreshedOnce) {
          try {
            await refreshTokens();
            refreshedOnce = true;
            attempt--;
            continue;
          } catch {
            clearAuthAndRedirect();
            throw err;
          }
        }
        if (!skipAuth && res.status === 401) {
          clearAuthAndRedirect();
        }
        throw err;
      }
      const buf = await res.arrayBuffer();
      const rawCt = res.headers.get('content-type') || '';
      const contentType = rawCt.split(';')[0].trim().toLowerCase();
      const contentDisposition = res.headers.get('content-disposition');
      const first = new Uint8Array(buf.byteLength ? buf.slice(0, 1) : new ArrayBuffer(0))[0];
      if (contentType.includes('json') || first === 0x7b || first === 0x5b) {
        const text = new TextDecoder().decode(buf);
        let msg = 'Export failed';
        try {
          const j = JSON.parse(text);
          msg = extractErrorMessage(j, msg);
        } catch {
          msg = text.slice(0, 400);
        }
        throw new Error(msg);
      }
      const blob = new Blob([buf], { type: rawCt || 'application/octet-stream' });
      return { blob, contentDisposition, contentType: rawCt };
    } catch (err) {
      clearTimeout(id);
      lastError = err;
      const networkLikeError = isNetworkLikeError(err);
      if (networkLikeError && urlIndex < requestUrls.length - 1) {
        urlIndex += 1;
        if (attempt < retries) {
          continue;
        }
      }
      if (networkLikeError && !triedWithoutDeviceHeaders) {
        triedWithoutDeviceHeaders = true;
        if (attempt < retries) {
          continue;
        }
      }
      if ((err.name === 'AbortError' || err.message?.includes('timeout') || networkLikeError) && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(4000, 200 * 2 ** attempt)));
        continue;
      }
      if (err.name === 'AbortError') throw new Error('Request timeout');
      throw err;
    }
  }
  throw lastError || new Error('Request failed');
}

function parseFilenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== 'string') return null;
  const star = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(cd);
  if (quoted?.[1]) return quoted[1].trim();
  const plain = /filename\s*=\s*([^;\n]+)/i.exec(cd);
  if (plain?.[1]) return plain[1].replace(/^["']|["']$/g, '').trim();
  return null;
}

const api = {
  get(path, options = {}) {
    return request(path, { ...options, method: 'GET' });
  },
  post(path, body, options = {}) {
    return request(path, { ...options, method: 'POST', body });
  },
  postBlob(path, body, options = {}) {
    return requestBlob(path, { ...options, method: 'POST', body });
  },
  put(path, body, options = {}) {
    return request(path, { ...options, method: 'PUT', body });
  },
  patch(path, body, options = {}) {
    return request(path, { ...options, method: 'PATCH', body });
  },
  delete(path, options = {}) {
    return request(path, { ...options, method: 'DELETE' });
  },
};

export default api;
export { baseURL, parseFilenameFromContentDisposition };
